// src/common/utils/type-formatter.js
// Utility for formatting citation type objects consistently across the UI

/**
 * Format a citation type for display with hierarchical structure.
 * @param {string|Object} type - The type (string, Precedent, LegalNorm, Academic, InCase, News, or Other object)
 * @returns {string} - Formatted type string (e.g., "Precedent > US Case law", "InCase > legal submission")
 */
export function formatCitationType(type) {
  if (!type) return '—';
  
  // Handle string types (legacy or simple types)
  if (typeof type === 'string') {
    return type;
  }
  
  // Handle object types
  if (typeof type === 'object') {
    // Handle Precedent object with origin
    if (type.origin) {
      return `Precedent > ${type.origin}`;
    }
    
    // Handle LegalNorm object with jurisdiction
    if (type.jurisdiction) {
      return `Legal Norm > ${type.jurisdiction}`;
    }
    
    // Handle objects with subtype (Academic, InCase, News, Other)
    if (type.subtype) {
      // Determine the parent type based on subtype
      if (type.subtype === 'academic') {
        return 'Academic > academic';
      } else if (type.subtype === 'legal submission' || type.subtype === 'factual exhibit') {
        return `InCase > ${type.subtype}`;
      } else if (type.subtype === 'news') {
        return 'News > news';
      } else if (type.subtype === 'website' || type.subtype === 'UN Docs' || type.subtype === 'other') {
        return `Other > ${type.subtype}`;
      } else {
        // Unknown subtype - just show it
        return type.subtype;
      }
    }
    
    // Handle stringified object - try to parse it
    if (type.toString && type.toString() === '[object Object]') {
      // Try to extract origin if it exists
      if ('origin' in type) {
        return `Precedent > ${type.origin}`;
      }
      // Try to extract jurisdiction if it exists
      if ('jurisdiction' in type) {
        return `Legal Norm > ${type.jurisdiction}`;
      }
      // Try to extract subtype if it exists
      if ('subtype' in type) {
        const subtype = type.subtype;
        if (subtype === 'academic') {
          return 'Academic > academic';
        } else if (subtype === 'legal submission' || subtype === 'factual exhibit') {
          return `InCase > ${subtype}`;
        } else if (subtype === 'news') {
          return 'News > news';
        } else if (subtype === 'website' || subtype === 'UN Docs' || subtype === 'other') {
          return `Other > ${subtype}`;
        } else {
          return subtype;
        }
      }
      
      // Last resort fallback: show JSON
      const keys = Object.keys(type);
      if (keys.length > 0) {
        return JSON.stringify(type);
      }
    }
  }
  
  // Final fallback
  return String(type);
}






